import api from './services/api.js'

let currentRoute = ''

// Шумные ошибки, которые не несут полезной информации
const IGNORED_PATTERNS = [
  'RenderedCameraImpl',           // Камера: пользователь свернул/переключился — ожидаемое поведение
  'Failed to register a ServiceWorker', // Сетевые проблемы при загрузке sw.js
  '[object Event]',              // Cross-origin ошибки без полезной информации
  'Script error.',               // Cross-origin без стека и деталей — скрипты, инжектированные in-app браузерами (Instagram и т.п.)
  'The operation was aborted',   // AbortError: запрос оборван уходом со страницы или сворачиванием
]

function isIgnored(message) {
  return IGNORED_PATTERNS.some(p => message.includes(p))
}

function sendError(message, stack, meta) {
  // Don't report in dev
  if (import.meta.env.DEV) {
    console.error('[ErrorTracking]', message, meta)
    return
  }

  const msg = String(message)
  if (isIgnored(msg)) return

  api.post('/errors/report', {
    message: msg.slice(0, 4000),
    stack: String(stack || '').slice(0, 8000),
    url: currentRoute || window.location.pathname,
    meta,
  }).catch(() => {
    // Silent — don't cause error loops
  })
}

// Отказ API. Зовётся из перехватчика axios в services/api.js — туда попадают
// все неудачные запросы, включая те, что код поймал сам. Раньше именно они и
// терялись: unhandledrejection их не видит, потому что у них есть catch.
// Событие без исключения — например, сорвавшаяся покупка в App Store: без
// отчета её видно только на телефоне (23.10.2026 первая покупка в TestFlight
// не оставила на сервере ни следа)
export function reportClientEvent(message, meta) {
  sendError(message, '', meta)
}

export function reportApiFailure(error) {
  const status = error?.response?.status

  // Интересуют только поломки на сервере. 4xx — штатный ответ бизнес-логики
  // (лимит токенов, нужна подписка), обрыв связи — не наша ошибка и слишком
  // шумно: мобильный интернет рвётся постоянно.
  if (!status || status < 500) return

  const method = (error.config?.method || 'get').toUpperCase()
  const endpoint = error.config?.url || ''

  sendError(`API ${status} ${method} ${endpoint}`, error.stack, {
    type: 'api',
    status,
    method,
    endpoint,
    serverMessage: error.response?.data?.error,  // что сервер показал человеку
  })
}

export function initErrorTracking(app, router) {
  // Track current route
  router.afterEach((to) => {
    currentRoute = to.fullPath
  })

  // Vue component errors
  app.config.errorHandler = (err, instance, info) => {
    console.error(err)
    sendError(err.message || String(err), err.stack, {
      hook: info,
      component: instance?.$options?.name || instance?.$options?.__name,
    })
  }

  // Unhandled JS errors
  window.addEventListener('error', (event) => {
    // Ignore script loading errors (CORS, ad-blockers, etc.)
    if (!event.message) return
    sendError(event.message, event.error?.stack, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message = reason?.message || String(reason)
    // Отказы API разбирает reportApiFailure() из перехватчика axios: сюда они
    // попадают без подробностей и только если их никто не поймал.
    if (reason?.isAxiosError) return
    sendError(message, reason?.stack, { type: 'unhandledrejection' })
  })
}
